import { useEffect, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import moment from "moment";
import { Alert, Descriptions, Input, message, Modal, Progress, Space, Table, Tag, Typography } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, StopOutlined, SwapOutlined } from "@ant-design/icons";
import { ButtonItem, ModalItem } from "../../../../Components/Items";

const { Text } = Typography;

// Dar de baja un lote capturado por error (p. ej. un código de Libro Virtual duplicado) y
// reemplazarlo en las ventas que lo recibieron. La baja es UNA petición; el reemplazo va
// venta por venta, en serie, para mostrar avance y no pelear el candado de asignación de
// códigos (backend: Controllers/bajaLotes.controllers.js).
//
// Si el lote ya está dado de baja (se entra por "Ventas pendientes"), se salta la baja y solo
// se reemplaza lo que haya quedado pendiente.

const ModalBajaLote = ({ visible, loteId, onCancel, empresaId, headersToken }) => {
  const [loading, setLoading] = useState(false);
  const [datos, setDatos] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [procesando, setProcesando] = useState(false);
  const [progreso, setProgreso] = useState({ hechas: 0, total: 0 });
  const [resultados, setResultados] = useState([]);
  const [avisoPoliza, setAvisoPoliza] = useState("");
  const [huboCambios, setHuboCambios] = useState(false);

  const consultar = () => {
    setLoading(true);
    return axios
      .post("lotes/consultarBajaLote", { empresaId, entradaId: loteId }, { headers: headersToken })
      .then((res) => {
        if (res.data.success === 2) {
          setDatos(res.data);
        } else {
          setDatos(null);
          message.warning(res.data.message || "No se pudo consultar el lote.");
        }
      })
      .catch(() => message.error("Error al consultar el lote"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (visible && loteId) {
      setMotivo("");
      setResultados([]);
      setProgreso({ hechas: 0, total: 0 });
      setAvisoPoliza("");
      setHuboCambios(false);
      consultar();
    }
    if (!visible) setDatos(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, loteId]);

  const ventasPorReemplazar = (datos?.ventas || []).filter((v) => !v.cancelada);

  const reemplazarEnVentas = async (ventas) => {
    setProcesando(true);
    setProgreso({ hechas: 0, total: ventas.length });

    const nuevos = [];
    for (let i = 0; i < ventas.length; i++) {
      const venta = ventas[i];
      let fila;
      try {
        const res = await axios.post(
          "lotes/reemplazarLoteVenta",
          { empresaId, ventaId: venta.ventaId, entradaId: loteId },
          { headers: headersToken }
        );
        fila = {
          ...venta,
          ok: res.data.success === 2 && res.data.completo !== false,
          mensaje: res.data.message || "",
          codigosNuevos: res.data.codigosNuevos || [],
          correoEnviado: res.data.correoEnviado === true,
          motivoCorreo: res.data.motivoCorreo || "",
        };
        if (res.data.success === 2) setHuboCambios(true);
      } catch (err) {
        fila = { ...venta, ok: false, mensaje: "Error de comunicación con el servidor", codigosNuevos: [] };
      }
      nuevos.push(fila);
      setProgreso({ hechas: i + 1, total: ventas.length });
    }

    // Un reintento reemplaza la fila anterior de la misma venta en vez de duplicarla.
    setResultados((previos) => {
      const porVenta = new Map(previos.map((r) => [String(r.ventaId), r]));
      nuevos.forEach((r) => porVenta.set(String(r.ventaId), r));
      return [...porVenta.values()];
    });
    setProcesando(false);
  };

  const ejecutarBaja = async () => {
    setProcesando(true);
    try {
      const res = await axios.post(
        "lotes/darDeBajaLote",
        { empresaId, entradaId: loteId, motivo: motivo.trim() },
        { headers: headersToken }
      );
      if (res.data.success !== 2) {
        message.error(res.data.message || "No se pudo dar de baja el lote.");
        return;
      }
      setHuboCambios(true);
      setAvisoPoliza(res.data.avisoPoliza || "");
      message.success("Lote dado de baja.");
      const ventas = (res.data.ventas || []).filter((v) => !v.cancelada);
      if (ventas.length > 0) await reemplazarEnVentas(ventas);
      await consultar();
    } catch (err) {
      message.error("Error de comunicación con el servidor");
    } finally {
      setProcesando(false);
    }
  };

  const darDeBaja = () => {
    if (!motivo.trim()) {
      message.warning("Escribe el motivo de la baja.");
      return;
    }
    const n = ventasPorReemplazar.length;
    Modal.confirm({
      title: "¿Dar de baja este lote?",
      content:
        n > 0
          ? `El lote sale del inventario y a ${n} venta(s) se les quitará el código ${datos.lote.numero}, se les entregará otro y se les mandará el correo. Esto no se puede deshacer.`
          : `El lote ${datos.lote.numero} sale del inventario. Esto no se puede deshacer.`,
      okText: "Dar de baja",
      cancelText: "Cancelar",
      // Sin devolver la promesa: el aviso se cierra de inmediato y el avance se ve en el modal.
      onOk: () => {
        ejecutarBaja();
      },
    });
  };

  const reemplazarPendientes = async (ventas) => {
    await reemplazarEnVentas(ventas);
    await consultar();
  };

  const fallidas = resultados.filter((r) => !r.ok);
  const lote = datos?.lote;

  const columnasVentas = [
    { title: "Folio", dataIndex: "folio", key: "folio", width: 110 },
    { title: "Cliente", dataIndex: "clienteNombre", key: "clienteNombre" },
    {
      title: "Correo",
      dataIndex: "correos",
      key: "correos",
      render: (correos) => (correos && correos.length > 0 ? correos.join(", ") : <Text type="danger">Sin correo</Text>),
    },
    { title: "Cant.", dataIndex: "cantidad", key: "cantidad", width: 60, align: "right" },
    {
      title: "",
      key: "estado",
      width: 120,
      render: (_, record) => (record.cancelada ? <Tag>Cancelada, no requiere</Tag> : null),
    },
  ];

  const columnasResultados = [
    { title: "Folio", dataIndex: "folio", key: "folio", width: 110 },
    { title: "Cliente", dataIndex: "clienteNombre", key: "clienteNombre" },
    {
      title: "Código nuevo",
      dataIndex: "codigosNuevos",
      key: "codigosNuevos",
      render: (codigos) => (codigos && codigos.length > 0 ? <Tag color="purple">{codigos.join(", ")}</Tag> : "-"),
    },
    {
      title: "Correo",
      key: "correo",
      render: (_, r) =>
        r.correoEnviado ? <Tag color="green">Enviado</Tag> : r.ok ? <Text type="warning">{r.motivoCorreo || "No enviado"}</Text> : "-",
    },
    {
      title: "Resultado",
      key: "resultado",
      render: (_, r) =>
        r.ok ? (
          <Text type="success"><CheckCircleOutlined /> Reemplazado</Text>
        ) : (
          <Text type="danger"><CloseCircleOutlined /> {r.mensaje}</Text>
        ),
    },
  ];

  const insuficientes =
    datos && !datos.yaDadoDeBaja && ventasPorReemplazar.length > 0 &&
    datos.codigosDisponibles < ventasPorReemplazar.reduce((s, v) => s + (v.cantidad || 0), 0);

  return (
    <ModalItem
      title="Dar de baja lote"
      visible={visible}
      onCancel={() => !procesando && onCancel(huboCambios)}
      width={900}
      spinning={loading}
      maskClosable={false}
    >
      {datos && lote && (
        <Space direction="vertical" style={{ width: "100%", paddingTop: 10 }} size={12}>
          <Descriptions size="small" bordered column={2}>
            <Descriptions.Item label="Número / código">
              <Text strong>{lote.numero || "-"}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Insumo">{datos.insumo?.descripcion || "-"}</Descriptions.Item>
            <Descriptions.Item label="Proyecto">{lote.proyectoNombre || "-"}</Descriptions.Item>
            <Descriptions.Item label="Caducidad">
              {lote.caducidad ? moment(lote.caducidad).format("DD/MM/YYYY") : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Cantidad">{lote.cantidad}</Descriptions.Item>
            <Descriptions.Item label="Vendido / Saldo">
              {lote.cantidadVendida} / {lote.saldo}
            </Descriptions.Item>
          </Descriptions>

          {datos.yaDadoDeBaja && (
            <Alert
              type="info"
              showIcon
              message={`Lote dado de baja${lote.fechaBaja ? ` el ${moment(lote.fechaBaja).format("DD/MM/YYYY")}` : ""}`}
              description={lote.motivoBaja ? `Motivo: ${lote.motivoBaja}` : undefined}
            />
          )}

          {!datos.yaDadoDeBaja && !datos.puedeDarDeBaja && (
            <Alert type="error" showIcon message="Este lote no se puede dar de baja" description={datos.motivoNoBaja} />
          )}

          {(datos.otrosLotes || []).length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`Hay ${datos.otrosLotes.length} lote(s) más con el número ${lote.numero}`}
              description={
                <>
                  <div style={{ marginBottom: 4 }}>
                    Revisa que estés dando de baja el que está mal. Al cliente que recibió el lote bueno no se le toca nada.
                  </div>
                  {datos.otrosLotes.map((o) => (
                    <div key={o._id}>
                      Capturado el {moment(o.createdAt).format("DD/MM/YYYY HH:mm")} · cantidad {o.cantidad} · vendido{" "}
                      {o.cantidadVendida} · saldo {o.saldo}
                      {o.concepto ? ` · ${o.concepto}` : ""}
                    </div>
                  ))}
                </>
              }
            />
          )}

          {insuficientes && (
            <Alert
              type="warning"
              showIcon
              message="No alcanzan los códigos disponibles"
              description={`Hay ${datos.codigosDisponibles} código(s) disponible(s) en este almacén para reemplazar. Las ventas que no alcancen se quedan con su código actual y aparecen en "Ventas pendientes" para reintentar cuando cargues más.`}
            />
          )}

          <div>
            <Text strong>
              {datos.yaDadoDeBaja ? "Ventas pendientes de reemplazo" : "Ventas que recibieron este lote"}:{" "}
              {ventasPorReemplazar.length}
            </Text>
            <Table
              size="small"
              rowKey={(r) => String(r.ventaId)}
              dataSource={datos.ventas || []}
              columns={columnasVentas}
              pagination={(datos.ventas || []).length > 10 ? { pageSize: 10, size: "small" } : false}
              style={{ marginTop: 6 }}
              locale={{ emptyText: "Ninguna venta tiene este lote" }}
            />
          </div>

          {!datos.yaDadoDeBaja && datos.puedeDarDeBaja && (
            <div>
              <Text strong>Motivo de la baja</Text>
              <Input.TextArea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                maxLength={500}
                rows={2}
                placeholder="Ej. Lote capturado dos veces en la recepción"
                disabled={procesando}
                style={{ marginTop: 6 }}
              />
            </div>
          )}

          {procesando && progreso.total > 0 && (
            <div>
              <Text>
                Reemplazando códigos: {progreso.hechas} de {progreso.total}
              </Text>
              <Progress percent={Math.round((progreso.hechas / progreso.total) * 100)} size="small" />
            </div>
          )}

          {avisoPoliza && <Alert type="error" showIcon message={avisoPoliza} />}

          {resultados.length > 0 && (
            <div>
              <Text strong>
                Resultado: {resultados.length - fallidas.length} reemplazada(s), {fallidas.length} con problema
              </Text>
              <Table
                size="small"
                rowKey={(r) => String(r.ventaId)}
                dataSource={resultados}
                columns={columnasResultados}
                pagination={resultados.length > 10 ? { pageSize: 10, size: "small" } : false}
                style={{ marginTop: 6 }}
              />
            </div>
          )}

          <div style={{ textAlign: "right", paddingBottom: 10 }}>
            <Space>
              {!datos.yaDadoDeBaja && datos.puedeDarDeBaja && (
                <ButtonItem rojo icon={<StopOutlined />} onClick={darDeBaja} disabled={procesando}>
                  {ventasPorReemplazar.length > 0
                    ? `Dar de baja y reemplazar en ${ventasPorReemplazar.length} venta(s)`
                    : "Dar de baja"}
                </ButtonItem>
              )}
              {datos.yaDadoDeBaja && fallidas.length > 0 && (
                <ButtonItem naranja icon={<SwapOutlined />} disabled={procesando} onClick={() => reemplazarPendientes(fallidas)}>
                  Reintentar fallidas ({fallidas.length})
                </ButtonItem>
              )}
              {datos.yaDadoDeBaja && fallidas.length === 0 && ventasPorReemplazar.length > 0 && (
                <ButtonItem morado icon={<SwapOutlined />} disabled={procesando} onClick={() => reemplazarPendientes(ventasPorReemplazar)}>
                  Reemplazar en {ventasPorReemplazar.length} venta(s) pendiente(s)
                </ButtonItem>
              )}
              <ButtonItem type="default" disabled={procesando} onClick={() => onCancel(huboCambios)}>
                Cerrar
              </ButtonItem>
            </Space>
          </div>
        </Space>
      )}
    </ModalItem>
  );
};

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ModalBajaLote);
